import { Stack } from "@mui/material";
import { useEffect, useRef } from "react";
import { PrimaryLinearProgress } from "react-common-ui-library";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  ACCOUNT_CREATION_ROUTE,
  ACCOUNT_SETUP_ROUTE,
  BACK_BUTTON_TEXT,
  CONSUMER_ACCOUNT_CREATION,
  EMAIL,
  H1,
  H3,
  IS_RECAPTCHA_ENABLED,
  MODE_ON_SUBMIT,
  PROGRESS_LABEL,
  SPACING_3_5,
  SPACING_4_5,
  SPACING_5_4,
  VERIFY_ACCOUNT_ROUTE,
} from "../../../Constants/CommonConstants";
import {
  REVIEW_YOUR_INFORMATION_COMPLETION_FRACTION,
  REVIEW_YOUR_INFORMATION_HEADING,
  REVIEW_YOUR_INFORMATION_PAGE_PROGRESS_VALUE,
} from "../../../Constants/ReviewYourInformationConstants";
import { StyledTypographyHeader } from "../../../Styles/Common/CommonStyles";
import GoogleReCaptcha from "../../Application/GoogleReCaptcha";
import AccountActionButtons from "../AccountActionButtons";
import ReviewAccountSetupSection from "./ReviewAccountSetupSection";
import ReviewNameAndContactSection from "./ReviewNameAndContactSection";
import { FormProvider, useForm } from "react-hook-form";
import { manageAccountCreationSelector } from "../../../Selectors/AccountCreationSelector";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAccountCreationApiResponse,
  postOTP,
  resetClientAccountSendOTPDescription,
} from "../../../Features/AccountCreationSlice";
import { OTP_SENT_SUCCESSFULLY } from "../../../Constants/VerifyAccountConstants";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import {
  SLICE_ACCOUNT_CREATION,
  THUNK_POST_OTP,
} from "../../../Constants/SliceAndThunkConstants";
import ConfigService from "../../../Services/ConfigService";

const isReCaptchaEnabled = ConfigService(IS_RECAPTCHA_ENABLED);

/**
 * @description contains information that is saved in  Name and contact page and Account setup .
 * @returns {ReactNode} Render the Name and contact section with First name, Last name, Date of birth, Preferred verification method, Phone number or Email.
 * And Account Setup section with Username and Password
 */
const ReviewYourInformation = () => {
  const navigate = useNavigate();

  const intl = useIntl();

  const dispatch = useDispatch();

  const reviewInformationRef = useRef();

  const {
    dateOfBirth,
    preferredContact,
    email,
    phone,
    userName,
    password,
    firstName,
    lastName,
    emailLanguage,
  } = useSelector((state) => manageAccountCreationSelector(state));

  const methods = useForm({
    mode: MODE_ON_SUBMIT,
  });

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(state, SLICE_ACCOUNT_CREATION, THUNK_POST_OTP)
  );

  const { isSuccess, description } = apiResponses;

  useEffect(() => {
    if (isSuccess && description === OTP_SENT_SUCCESSFULLY) {
      navigate(`/${ACCOUNT_CREATION_ROUTE}/${VERIFY_ACCOUNT_ROUTE}`, {
        state: { previousScreen: CONSUMER_ACCOUNT_CREATION },
      });
    }
    dispatch(clearAccountCreationApiResponse(THUNK_POST_OTP));
  }, [isSuccess, navigate, dispatch, description]);

  const handleNextStep = async () => {
    const token = isReCaptchaEnabled
      ? await reviewInformationRef.current.onSubmitWithReCAPTCHA()
      : null;
    if (token || !isReCaptchaEnabled) {
      const data = {
        dob: dateOfBirth,
        prefVerificationMethod: preferredContact,
        phoneNumber: phone,
        email: email,
        userName: userName,
        password: password,
        reCaptchaTkn: token,
        firstName: firstName,
        lastName: lastName,
      };
      if (preferredContact === EMAIL) {
        data.prefEmailLanguage = emailLanguage.description;
      }
      dispatch(postOTP(data));
      dispatch(resetClientAccountSendOTPDescription());
    }
  };

  const navigateToAccountHome = () => {
    navigate(`/${ACCOUNT_CREATION_ROUTE}`);
  };

  const navigateToAccountSetupPage = () => {
    navigate(`/${ACCOUNT_CREATION_ROUTE}/${ACCOUNT_SETUP_ROUTE}`);
  };

  return (
    <Stack>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleNextStep)}>
          <Stack gap={5}>
            <Stack gap={SPACING_4_5}>
              <PrimaryLinearProgress
                progressValue={REVIEW_YOUR_INFORMATION_PAGE_PROGRESS_VALUE}
                progressLabel={PROGRESS_LABEL}
                currentPage={REVIEW_YOUR_INFORMATION_COMPLETION_FRACTION}
                backButtonText={BACK_BUTTON_TEXT}
                backButtonOnClickHandler={navigateToAccountSetupPage}
              />
              <Stack gap={SPACING_3_5}>
                <StyledTypographyHeader
                  id={REVIEW_YOUR_INFORMATION_HEADING}
                  component={H1}
                  variant={H3}
                >
                  {intl.formatMessage({
                    id: REVIEW_YOUR_INFORMATION_HEADING,
                  })}
                </StyledTypographyHeader>
                <Stack gap={SPACING_5_4}>
                  <ReviewNameAndContactSection />
                  <ReviewAccountSetupSection />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <AccountActionButtons cancelClickHandler={navigateToAccountHome} />
        </form>
      </FormProvider>
      {isReCaptchaEnabled ? (
        <GoogleReCaptcha ref={reviewInformationRef} />
      ) : null}
    </Stack>
  );
};

export default ReviewYourInformation;
